import { useState, useCallback } from 'react';
import { formatErrorForDisplay } from '../../../utils/errorHandling';

/**
 * React hook for error state management in support components
 */
export const useErrorHandler = () => {
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const handleError = useCallback((error, context = {}) => {
    const formattedError = formatErrorForDisplay(error, {
      showTechnicalDetails: process.env.NODE_ENV === 'development'
    });
    
    setError({
      ...formattedError,
      originalError: error,
      context,
      timestamp: Date.now()
    });
    setIsRetrying(false);
  }, []);
  
  const clearError = useCallback(() => {
    setError(null);
    setIsRetrying(false);
    setRetryCount(0);
  }, []);
  
  const retry = useCallback(async (retryFn) => {
    if (!retryFn) return;
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    try {
      await retryFn();
      clearError();
    } catch (retryError) {
      handleError(retryError, { ...error?.context, isRetry: true });
    } finally {
      setIsRetrying(false);
    }
  }, [error, handleError, clearError]);
  
  return {
    error,
    isRetrying,
    retryCount,
    handleError,
    clearError,
    retry,
    hasError: !!error,
    canRetry: !!error?.canRetry
  };
};

export default useErrorHandler;